/// <reference path="../../globals.d.ts" />

import Vue, { CreateElement } from '@src/vue'

import { Component, Watch } from 'vue-property-decorator'

import camelcaseKeys from 'camelcase-keys'

import { Spacer } from '@src/elements/spacer/spacer'
import { Divider } from '@src/elements/divider/divider'

import { Sidebar, NavItem, ProfileNavItem } from '@src/components/sidebar/sidebar'
import { Logo } from '@src/elements/logo/logo'
import { DefaultValues } from '@src/util/default-values'
import ApiClient from '@src/lib/api_client'
import { ExerciseResponse } from '@src/data/exercise-response'
import { API } from '@src/lib/api_path'
import EventBus from '@src/lib/event_bus'
import { Loader } from '@src/components/loading-screen/loader/loader'
import Club from '@src/data/club'
import { MemberProfile } from '@src/data/member-profile'
import { TermsAndConditions } from '@src/routes/members/terms-conditions'
import { convertDateTo } from '@src/util/date-format'

import 'element-ui/lib/theme-chalk/index.css'
import './member-platform.sass'
import { PortraitHeader } from '@src/components/header/portrait'
import { Subtitle1 } from '@src/elements/typography/typography'
import { ConfirmModal } from '@src/elements/modal/confirm-modal'
import { NotificationTypes } from '@src/util/constants'
import SessionManager from '@src/lib/session_manager'
import { Icon } from '@src/elements/icon/icon'
import { PortraitSettings } from '@src/components/portrait-settings'

type ExercisesResponse = { exercises: ExerciseResponse[] }
type MemberResponse = { profile: MemberProfile }

@Component({
  computed: {
    member () {
      return this.$store.state.memberProfile
    },
    exercises () {
      return this.$store.state.availableExercises
    },
    locale () {
      return this.$store.state.i18n.locale
    },
    isSidebarVisible () {
      return this.$store.state.isSidebarVisible
    }
  }
})
export class MemberPlatformRoute extends Vue {

  member: MemberProfile

  private apiClient: ApiClient = new ApiClient()

  private exercises: ExerciseResponse[]

  private loading: boolean = true

  private isSidebarVisible: boolean

  private logoutConfirmVisible: boolean = false

  private portraitSettingsAreVisible: boolean = false

  constructor () {
    super()
  }

  @Watch('$route')
  scrollUpOnRouteChange () {
    const memberContent = this.$refs.memberContent as Element
    if (memberContent && memberContent!.scrollTop) memberContent.scrollTop = 0
  }

  @Watch('isSidebarVisible')
  closePortraitSettings () {
    if (this.portraitSettingsAreVisible && !this.isSidebarVisible) {
      this.portraitSettingsAreVisible = false
    }
  }

  goToRoute (path) {
    return () => {
      this.$store.dispatch('updateSidebarVisibility', false)
      this.$router.push(path)
    }
  }

  isActiveRoute (path) {
    return !!this.$route.path.match(path)
  }

  showLogoutConfirm () {
    this.logoutConfirmVisible = !this.logoutConfirmVisible
  }

  goToAccount () {
    if (SessionManager.Instance.isManager()) {
      this.$router.push('/trainers/platform/account')
    } else {
      this.portraitSettingsAreVisible = false
      this.$store.dispatch('updateSidebarVisibility', false)
      this.$router.push(`/members/${this.$route.params.id}/account`)
    }
  }

  logout (confirm: boolean) {
    if (confirm === true) {
      SessionManager.Instance.logout()
      this.$store.dispatch('logout')
      this.$store.dispatch('updateSidebarVisibility', false)
      this.$router.push('/')
    }
    this.logoutConfirmVisible = false
  }

  updatePortraitSettingsState () {
    this.portraitSettingsAreVisible = !this.portraitSettingsAreVisible
  }

  closeSideBar () {
    this.$store.dispatch('updateSidebarVisibility', false)
  }

  render (h: CreateElement) {
    if (this.loading) {
      return <Loader />
    }

    const baseRoute = `/members/${this.member.id}`

    return (
      <div class='platform-root'>
        <PortraitHeader />
        <div class='platform-page'>

          <div class={`sidebar-container member-sidebar ${this.isSidebarVisible ? 'open' : 'close'}`}>
            <Sidebar ref={'sidebar-ref'}>
              <Logo type='responsive' />
              <NavItem icon={'sidebar/training'} label={this.$t('PAGE.MEMBERS.DETAIL.TABS.TRAINING')}
                       click={this.goToRoute(`${baseRoute}/training`)}
                       selected={this.isActiveRoute(`${baseRoute}/training`)} />
              <NavItem id='tab-status-id' icon={'sidebar/status'} label={this.$t('PAGE.MEMBERS.DETAIL.TABS.STATUS')}
                       click={this.goToRoute(`${baseRoute}/status`)} selected={this.isActiveRoute(`${baseRoute}/status`)} />
              <NavItem icon={'sidebar/history'} label={this.$t('HISTORY')}
                       click={this.goToRoute(`${baseRoute}/history`)}
                       selected={this.isActiveRoute(`${baseRoute}/history`)} />
              <NavItem icon={'sidebar/activities'} label={this.$t('PAGE.MEMBERS.DETAIL.TABS.ACTIVITIES')}
                       click={this.goToRoute(`${baseRoute}/member_activities`)}
                       selected={this.isActiveRoute(`${baseRoute}/member_activities`)} />
              {/*Hide health limitations*/}
              {/*<NavItem icon={'sidebar/health'} label={this.$t('PAGE.MEMBERS.DETAIL.TABS.HEALTH')}*/}
              {/*         click={this.goToRoute(`${baseRoute}/health`)} selected={this.isActiveRoute(`${baseRoute}/health`)} />*/}
              <NavItem icon={'sidebar/body'} label={this.$t('PAGE.MEMBERS.DETAIL.TABS.BODY_MEASUREMENTS')}
                       click={this.goToRoute(`${baseRoute}/body-measurements`)}
                       selected={this.isActiveRoute(`${baseRoute}/body-measurements`)} />
              <NavItem icon={'sidebar/checkups'} label={this.$t('PAGE.MEMBERS.DETAIL.TABS.CHECKUPS')}
                       click={this.goToRoute(`${baseRoute}/checkups`)}
                       selected={this.isActiveRoute(`${baseRoute}/checkups`)} />
              <NavItem id='tab-my-club-id' icon={'sidebar/my-club'} label={this.$t('PAGE.MEMBERS.DETAIL.TABS.MY_CLUB')}
                       click={this.goToRoute(`${baseRoute}/my-club`)} selected={this.isActiveRoute(`${baseRoute}/my-club`)} />
              <NavItem icon={'sidebar/support'} label={this.$t('SUPPORT.SECTION')}
                       click={this.goToRoute(`${baseRoute}/contact`)}
                       selected={this.isActiveRoute(`${baseRoute}/contact`)} />

              <Spacer />

              <Divider slot={'bottom'} />

              <ProfileNavItem label={this.member.fullName} slot={'bottom'} />
              <PortraitSettings label={this.member.fullName} callback={this.updatePortraitSettingsState} slot={'bottom'} />

            </Sidebar>
          </div>

          {this.member.termsReacceptanceRequired ?
            <TermsAndConditions acceptCallback={() => this.loadMember()} memberId={this.member.id} />
            :
            <div class='content member-content' ref='memberContent' onClick={this.closeSideBar}>
              <router-view />
            </div>
          }

          <div
            class='version-release-date'>{WEB_RELEASE_DATE ? `Release date: ${convertDateTo(WEB_RELEASE_DATE, 'DD/MM/YYYY HH:mm')}` : /* istanbul ignore next: WEB_RELEASE_DATE is a build constant, only empty in production builds */ ''}
          </div>

          <ConfirmModal show={this.logoutConfirmVisible}
                        closeCallback={this.logout}
                        message={this.$t('PAGE.LOGOUT.CONFIRM')}
                        type={NotificationTypes.CONFIRM} />

          {this.portraitSettingsAreVisible && (
            <div class='portrait-settings-options'>
              <div class='settings-back-arrow-wrapper'>
                <Icon name={'util/back-arrow'} class='logo' click={this.updatePortraitSettingsState} />
              </div>
              <div onClick={this.goToAccount} class='profile-option' ref='accountBtn'>
                <Subtitle1>{this.$t('PAGE.MEMBERS.ACCOUNT.ACCOUNT_SETTINGS')}</Subtitle1>
              </div>
              <div onClick={this.showLogoutConfirm} class='profile-option' ref='logoutBtn'>
                <Subtitle1>{this.$t('PAGE.LOGOUT.SUBMIT_BUTTON')}</Subtitle1>
              </div>
            </div>
          )}

        </div>
      </div>)
  }

  created () {
    EventBus.on('reloadSelectedMember', this.loadMember)

    // load the default data to be used all over the platform child components
    return Promise.all([
      this.loadMember(),
      DefaultValues.loadExerciseTags(this.$store),
      DefaultValues.loadActivities(this.$store),
      this.loadAllExercises()
    ]).then(() => {
      this.translateExerciseNames()
      this.loading = false
    })
  }

  beforeDestroy () {
    EventBus.off('reloadSelectedMember', this.loadMember)
  }

  private loadMember () {
    if (this.$route.params.id) {
      return this.apiClient.get<MemberResponse>(API.MEMBERS_API.PROFILE)
        .then((response: MemberResponse) => {
          this.$store.dispatch('setProfile', new MemberProfile(camelcaseKeys(response.profile)))
          this.$store.dispatch('setClub', new Club(camelcaseKeys(response.profile.club, { deep: true })))
        })
        .catch(error => console.error('failed to retrieve member', error))
    }
  }

  private loadAllExercises () {
    if (this.$store.state.availableExercises.length === 0) {
      // load both station dependent and home exercises
      return this.apiClient.get<ExercisesResponse>(`${API.MEMBERS_API.EXERCISES}?include_home_exercises=true`)
        .then((response) => {
          const exercises = response.exercises.map((attrs) => new ExerciseResponse(camelcaseKeys(attrs)))
          this.$store.dispatch('setAvailableExercises', exercises)
        })
        .catch((error) => {
          console.error('failed to retrieve exercises', error)
        })
    }
  }

  @Watch('locale')
  private translateExerciseNames () {
    if (this.$i18n && this.exercises) {
      let exercises = this.exercises.map(exercise => {
        if (!this.$te('EXERCISE[' + exercise.number + '].NAME')) exercise.setTranslatedName('')

        if (this.$te('EXERCISE[' + exercise.number + '].NAME')) {
          exercise.setTranslatedName(this.$t('EXERCISE[' + exercise.number + '].NAME'))
        }
        return exercise
      })
      this.$store.dispatch('setAvailableExercises', exercises)
    }
  }
}
